/**
 * UnreconcileConfirmDialog.js
 * Dialog de confirmação para remoção de conciliação com informações detalhadas
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Warning as WarningIcon,
  LinkOff as UnlinkIcon,
  Info as InfoIcon,
  Receipt as JournalIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../../helpers/formatCurrency';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const UnreconcileConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  reconciliationData,
  item,
  isLoading = false,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!reconciliationData || !item) {
    return null;
  }

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return 'N/A';
    }
  };

  const isTransaction = !!item.transactionHash;
  const journalEntries = reconciliationData.journalEntries || [];
  const bankTransactions = reconciliationData.bankTransactions || [];

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: 'warning.light',
              color: 'warning.main',
            }}
          >
            <WarningIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Confirmar Remoção de Conciliação
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação removerá permanentemente a conciliação
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {/* Informações da Conciliação */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="subtitle2" gutterBottom>
              Detalhes da Conciliação
            </Typography>
            <Stack direction="row" spacing={4} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  ID da Conciliação
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {reconciliationData.reconciliationId?.substring(0, 8)}...
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Data da Conciliação
                </Typography>
                <Typography variant="body2">
                  {formatDate(reconciliationData.reconciledAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tipo
                </Typography>
                <Typography variant="body2">
                  {reconciliationData.reconciliationType || 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Alert>

          {/* Item Atual */}
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {isTransaction ? <BankIcon fontSize="small" /> : <JournalIcon fontSize="small" />}
              {isTransaction ? 'Extrato Bancário Selecionado' : 'Lançamento Contábil Selecionado'}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isTransaction
                      ? formatDate(item.datePosted)
                      : formatDate(item.dateVencimento || item.dateCompetencia)}
                  </Typography>
                </Box>
                <Chip
                  label={formatCurrency(Math.abs(isTransaction ? item.amount : item.value))}
                  color={
                    isTransaction
                      ? item.amount > 0
                        ? 'success'
                        : 'error'
                      : item.isInput
                      ? 'success'
                      : 'error'
                  }
                  sx={{ fontWeight: 'bold' }}
                />
              </Stack>
            </Paper>
          </Box>

          {/* Lista de Itens Afetados */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Itens que serão Desconciliados
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {journalEntries.map((entry, index) => (
                    <TableRow key={`journal-${index}`}>
                      <TableCell>
                        <Chip
                          icon={<JournalIcon />}
                          label="Lançamento"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {entry.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(entry.dateVencimento)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={entry.isInput ? 'success.main' : 'error.main'}
                          fontWeight="600"
                        >
                          {formatCurrency(Math.abs(entry.value))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {bankTransactions.map((transaction, index) => (
                    <TableRow key={`bank-${index}`}>
                      <TableCell>
                        <Chip
                          icon={<BankIcon />}
                          label="Extrato"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transaction.datePosted)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                          fontWeight="600"
                        >
                          {formatCurrency(Math.abs(transaction.amount))}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Aviso */}
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Atenção:</strong> Esta ação não pode ser desfeita. Todos os itens relacionados
              a esta conciliação voltarão ao status "Pendente" e precisarão ser conciliados
              novamente se necessário.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isProcessing} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <UnlinkIcon />}
          sx={{ minWidth: 140 }}
        >
          {isProcessing ? 'Processando...' : 'Desconciliar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnreconcileConfirmDialog;
